import React, { useState } from "react";
import '../css/Registerform.css';
import { useEffect } from "react";

function Registerform() {
  const [step, setStep] = useState(1);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    dob: "",
    address: "",
    state: "",
    guardianName: "",
    guardianRelation: "",
    guardianPhone: "",
    profilePhoto: null,
    institution: "",
    program: "",
    department: "",
    year: "",
    instituteLocation: "",
    instituteState: "",
    currentSemesterCgpa: "",
    UG: "",
    cgpa12: "",
    board12: "",
    cgpa10: "",
    board10: "",
    adhaar_id: "",
    apaar_id: "",
    student_college_idcard: null,
    documents: null,
    role: "student"
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otherProgram, setOtherProgram] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Handle program field specially for "Other" option
    if (name === 'program') {
      if (value === 'other') {
        setShowOtherInput(true);
        // Don't set formData.program yet, wait for user input
        // Clear the program value when "Other" is selected
        setFormData({
          ...formData,
          program: ""
        });
      } else {
        setShowOtherInput(false);
        setOtherProgram("");
        // Update form data with selected program
        setFormData({
          ...formData,
          [name]: value,
        });
      }
      return;
    }

    // Handle other program input
    if (name === 'otherProgram') {
      setOtherProgram(value);
      // Update the program field in formData with the custom value
      setFormData({
        ...formData,
        program: value,
      });
      return;
    }

    // If it's a file input, validate before setting state
    if (files && files[0]) {
      const file = files[0];

      // File validation for profile photo
      if (name === 'profilePhoto') {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          setErrors({
            ...errors,
            [name]: 'Only JPEG, JPG, PNG, GIF, and WebP images are allowed'
          });
          return;
        }

        const maxSize = 50 * 1024;
        if (file.size > maxSize) {
          setErrors({
            ...errors,
            [name]: `File size must be less than 50KB. Current size: ${(file.size / 1024).toFixed(2)}KB`
          });
          return;
        }
      }

      // File validation for college ID card
      if (name === 'student_college_idcard') {
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          setErrors({
            ...errors,
            [name]: `File size must be less than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
          });
          return;
        }
      }

      // File validation for documents
      if (name === 'documents') {
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          setErrors({
            ...errors,
            [name]: `File size must be less than 10MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
          });
          return;
        }
      }
    }

    // Clear any previous error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }

    // Update form data for non-program fields
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await fetch("/api/students/allow_reg_status", {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to check registration status");
        }

        const data = await response.json();
        setRegistrationOpen(data.allow_reg);
      } catch (error) {
        console.error("Error checking registration status:", error);
        setRegistrationOpen(false);
      } finally {
        setLoadingStatus(false);
      }
    };

    checkRegistrationStatus();
  }, []);

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = "Name is required.";
      if (!/^[0-9]{10}$/.test(formData.phone)) newErrors.phone = "Valid 10-digit phone number is required.";
      if (!formData.dob) newErrors.dob = "Date of birth is required.";
      if (!formData.address.trim()) newErrors.address = "Address is required.";
      if (!formData.state.trim()) newErrors.state = "State is required.";
      if (!formData.guardianName.trim()) newErrors.guardianName = "Guardian name is required.";
      if (!formData.guardianRelation.trim()) newErrors.guardianRelation = "Guardian relation is required.";
      if (!/^[0-9]{10}$/.test(formData.guardianPhone)) newErrors.guardianPhone = "Valid 10-digit guardian phone number is required.";
    } else if (step === 2) {
      if (!formData.profilePhoto) newErrors.profilePhoto = "Profile photo is required.";
    } else if (step === 3) {
      if (!formData.institution.trim()) newErrors.institution = "Institution is required.";
      if (!formData.program.trim()) newErrors.program = "Program is required.";
      if (!formData.department.trim()) newErrors.department = "Department is required.";
      if (!formData.year.trim()) newErrors.year = "Year is required.";
      if (!formData.instituteLocation.trim()) newErrors.instituteLocation = "Institute location is required.";
      if (!formData.instituteState.trim()) newErrors.instituteState = "Institute state is required.";

      // Validate numeric fields
      if (!formData.program.trim()) {
        newErrors.program = "Program is required.";
      }
      // Note: We don't need a special case for "other" because formData.program 
      // will contain the custom value entered by the user
      if (!formData.currentSemesterCgpa.trim()) {
        newErrors.currentSemesterCgpa = "Current semester CGPA is required.";
      } else {
        const cgpa = parseFloat(formData.currentSemesterCgpa);
        if (isNaN(cgpa) || cgpa < 0 || cgpa > 100) {
          newErrors.currentSemesterCgpa = "Valid percentage (0-100) is required.";
        }
      }

      if (!formData.cgpa12.trim()) {
        newErrors.cgpa12 = "12th Percentage is required.";
      } else {
        const cgpa12 = parseFloat(formData.cgpa12);
        if (isNaN(cgpa12) || cgpa12 < 0 || cgpa12 > 100) {
          newErrors.cgpa12 = "Valid percentage (0-100) is required.";
        }
      }

      if (!formData.board12.trim()) newErrors.board12 = "12th board is required.";

      if (!formData.cgpa10.trim()) {
        newErrors.cgpa10 = "10th Percentage is required.";
      } else {
        const cgpa10 = parseFloat(formData.cgpa10);
        if (isNaN(cgpa10) || cgpa10 < 0 || cgpa10 > 100) {
          newErrors.cgpa10 = "Valid percentage (0-100) is required.";
        }
      }

      if (!formData.board10.trim()) newErrors.board10 = "10th board is required.";
      // if (!formData.UG.trim()) newErrors.UG = "UG details are required.";
    } else if (step === 4) {
      if (!formData.adhaar_id.trim()) newErrors.adhaar_id = "Aadhaar number is required.";
      if (!formData.apaar_id.trim()) newErrors.apaar_id = "APAAR ID is required.";
      if (!/^[0-9]{12}$/.test(formData.adhaar_id)) newErrors.adhaar_id = "Valid 12-digit Aadhaar number is required.";
      if (!/^[0-9]{12}$/.test(formData.apaar_id)) newErrors.apaar_id = "Valid 12-digit APAAR ID is required.";
      if (!formData.student_college_idcard) newErrors.student_college_idcard = "Student college ID card is required.";
      if (!formData.documents) newErrors.documents = "Documents are required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) setStep(step + 1);
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== 5) return;

    if (!window.confirm("Are you sure you want to submit your registration?")) return;

    setIsSubmitting(true);

    try {
      const form = new FormData();
      // Basic info
      form.append("name", formData.name);
      form.append("phone", formData.phone);
      form.append("dob", formData.dob);
      form.append("address", formData.address);
      form.append("state", formData.state);
      form.append("guardianName", formData.guardianName);
      form.append("guardianRelation", formData.guardianRelation);
      form.append("guardianPhone", formData.guardianPhone);

      // Academic info
      form.append("institution", formData.institution);
      form.append("program", formData.program);
      form.append("department", formData.department);
      form.append("year", formData.year);
      form.append("instituteLocation", formData.instituteLocation);
      form.append("instituteState", formData.instituteState);
      form.append("currentSemesterCgpa", parseFloat(formData.currentSemesterCgpa));
      form.append("UG", formData.UG);
      form.append("cgpa12", parseFloat(formData.cgpa12));
      form.append("board12", formData.board12);
      form.append("cgpa10", parseFloat(formData.cgpa10));
      form.append("board10", formData.board10);

      // IDs
      form.append("adhaar_id", formData.adhaar_id);
      form.append("apaar_id", formData.apaar_id);

      // Files - IMPORTANT: field names must match backend parameter names
      if (formData.profilePhoto) form.append("profilePhoto", formData.profilePhoto);
      if (formData.student_college_idcard) form.append("student_college_idcard", formData.student_college_idcard);
      if (formData.documents) form.append("documents", formData.documents);

      const response = await fetch("/api/students/register", {
        method: "POST",
        body: form,
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        console.error("Server error:", data);
        throw new Error(data.detail || data.message || JSON.stringify(data) || "Registration failed");
      }

      // Reset form
      // Reset form
      setFormData({
        name: "",
        phone: "",
        dob: "",
        address: "",
        state: "",
        guardianName: "",
        guardianRelation: "",
        guardianPhone: "",
        profilePhoto: null,
        institution: "",
        program: "",
        department: "",
        year: "",
        instituteLocation: "",
        instituteState: "",
        currentSemesterCgpa: "",
        UG: "",
        cgpa12: "",
        board12: "",
        cgpa10: "",
        board10: "",
        adhaar_id: "",
        apaar_id: "",
        student_college_idcard: null,
        documents: null,
        role: "student"
      });

      // Reset "Other" program states
      setOtherProgram("");
      setShowOtherInput(false);

      alert("Registration successful! Please pay the registration fee to complete your application.");
      // alert("Registration successful!");
      window.location.href = "/candidatedashboard";
    } catch (error) {
      alert("Registration failed: " + error.message);
      console.error("Registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!registrationOpen) {
    return (
      <div className="register-container">
        <div className="registration-closed-message">
          <h2>Registrations are currently closed</h2>
          <p>We will notify you via email when registrations open.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="progress-bar">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Personal</div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Photo</div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Academic</div>
        <div className={`step ${step >= 4 ? 'active' : ''}`}>4. IDs</div>
        <div className={`step ${step >= 5 ? 'active' : ''}`}>5. Review</div>
      </div>
      <form onSubmit={handleSubmit} className="registration-form">
        {/* Step 1: Personal */}
        {step === 1 && (
          <div className="form-step">
            <h2>Personal Information</h2>
            <div className="form-group">
              <label>Full Name*</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={errors.name ? 'error' : ''} />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>Phone Number*</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={errors.phone ? 'error' : ''} />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
            <div className="form-group">
              <label>Date of Birth*</label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={errors.dob ? 'error' : ''} />
              {errors.dob && <span className="error-text">{errors.dob}</span>}
            </div>
            <div className="form-group">
              <label>Address*</label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} className={errors.address ? 'error' : ''} />
              {errors.address && <span className="error-text">{errors.address}</span>}
            </div>
            <div className="form-group">
              <label>State*</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} className={errors.state ? 'error' : ''} />
              {errors.state && <span className="error-text">{errors.state}</span>}
            </div>
            <div className="form-group">
              <label>Guardian Name*</label>
              <input type="text" name="guardianName" value={formData.guardianName} onChange={handleChange} className={errors.guardianName ? 'error' : ''} />
              {errors.guardianName && <span className="error-text">{errors.guardianName}</span>}
            </div>
            <div className="form-group">
              <label>Guardian Relation*</label>
              <input type="text" name="guardianRelation" value={formData.guardianRelation} onChange={handleChange} className={errors.guardianRelation ? 'error' : ''} />
              {errors.guardianRelation && <span className="error-text">{errors.guardianRelation}</span>}
            </div>
            <div className="form-group">
              <label>Guardian Phone*</label>
              <input type="tel" name="guardianPhone" value={formData.guardianPhone} onChange={handleChange} className={errors.guardianPhone ? 'error' : ''} />
              {errors.guardianPhone && <span className="error-text">{errors.guardianPhone}</span>}
            </div>

          </div>
        )}

        {/* Step 2: Photo */}
        {step === 2 && (
          <div className="form-step">
            <h2>Passport size  Photo</h2>
            <div className="form-group">
              <label>Upload Passport size Photo* (Max: 50KB, JPEG/PNG only)</label>
              <input
                type="file"
                name="profilePhoto"
                onChange={handleChange}
                accept=".jpg,.jpeg,.png,.gif,.webp"
                className={errors.profilePhoto ? 'error' : ''}
              />
              {formData.profilePhoto && (
                <div className="file-info">
                  <p>Selected: {formData.profilePhoto.name} ({(formData.profilePhoto.size / 1024).toFixed(2)} KB)</p>
                  <img
                    src={URL.createObjectURL(formData.profilePhoto)}
                    alt="Preview"
                  />
                </div>
              )}
              {errors.profilePhoto && <span className="error-text">{errors.profilePhoto}</span>}
            </div>
          </div>
        )}

        {/* Step 3: Academic */}
        {step === 3 && (
          <div className="form-step">
            <h2>Academic Information</h2>
            <div className="form-group">
              <label>Institution*</label>
              <input type="text" name="institution" value={formData.institution} onChange={handleChange} className={errors.institution ? 'error' : ''} />
              {errors.institution && <span className="error-text">{errors.institution}</span>}
            </div>
            <div className="form-group">
              <label>Program*</label>
              <select
                name="program"
                value={showOtherInput ? 'other' : formData.program}
                onChange={handleChange}
                className={errors.program ? 'error' : ''}
              >
                <option value="">Select Program</option>
                <option value="btech">B.Tech</option>
                <option value="mtech">M.Tech</option>
                <option value="bsc">B.Sc</option>
                <option value="msc">M.Sc</option>
                <option value="other">Other</option>
              </select>

              {/* Show text input when "Other" is selected */}
              {showOtherInput && (
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Please specify your program:</label>
                  <input
                    type="text"
                    name="otherProgram"
                    value={otherProgram}
                    onChange={handleChange}
                    placeholder="Enter your program name"
                    className={errors.program ? 'error' : ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '5px'
                    }}
                  />
                  {!otherProgram && errors.program && (
                    <span className="error-text">{errors.program}</span>
                  )}
                </div>
              )}

              {!showOtherInput && errors.program && <span className="error-text">{errors.program}</span>}
            </div>
            <div className="form-group">
              <label>Branch*</label>
              <input type="text" name="department" value={formData.department} onChange={handleChange} className={errors.department ? 'error' : ''} />
              {errors.department && <span className="error-text">{errors.department}</span>}
            </div>
            <div className="form-group">
              <label>Year of Passing*</label>
              <input type="text" name="year" value={formData.year} onChange={handleChange} className={errors.year ? 'error' : ''} />
              {errors.year && <span className="error-text">{errors.year}</span>}
            </div>
            <div className="form-group">
              <label>Institute Location*</label>
              <input type="text" name="instituteLocation" value={formData.instituteLocation} onChange={handleChange} className={errors.instituteLocation ? 'error' : ''} />
              {errors.instituteLocation && <span className="error-text">{errors.instituteLocation}</span>}
            </div>
            <div className="form-group">
              <label>Institute State*</label>
              <input type="text" name="instituteState" value={formData.instituteState} onChange={handleChange} className={errors.instituteState ? 'error' : ''} />
              {errors.instituteState && <span className="error-text">{errors.instituteState}</span>}
            </div>
            <div className="form-group">
              <label>Current Semester Cgpa*</label>
              <input type="number" step="0.01" min="0" max="100" name="currentSemesterCgpa" value={formData.currentSemesterCgpa} onChange={handleChange} className={errors.currentSemesterCgpa ? 'error' : ''} />
              {errors.currentSemesterCgpa && <span className="error-text">{errors.currentSemesterCgpa}</span>}
            </div>
            <div className="form-group">
              <label>UG Cgpa (For PG Students)</label>
              <input type="number" step="0.01" min="0" max="100" name="UG" value={formData.UG} onChange={handleChange} className={errors.UG ? 'error' : ''} />
              {errors.UG && <span className="error-text">{errors.UG}</span>}
            </div>
            <div className="form-group">
              <label>12th Percentage*</label>
              <input type="number" step="0.01" min="0" max="100" name="cgpa12" value={formData.cgpa12} onChange={handleChange} className={errors.cgpa12 ? 'error' : ''} />
              {errors.cgpa12 && <span className="error-text">{errors.cgpa12}</span>}
            </div>
            <div className="form-group">
              <label>12th Board*</label>
              <input type="text" name="board12" value={formData.board12} onChange={handleChange} className={errors.board12 ? 'error' : ''} />
              {errors.board12 && <span className="error-text">{errors.board12}</span>}
            </div>
            <div className="form-group">
              <label>10th Percentage*</label>
              <input type="number" step="0.01" min="0" max="100" name="cgpa10" value={formData.cgpa10} onChange={handleChange} className={errors.cgpa10 ? 'error' : ''} />
              {errors.cgpa10 && <span className="error-text">{errors.cgpa10}</span>}
            </div>
            <div className="form-group">
              <label>10th Board*</label>
              <input type="text" name="board10" value={formData.board10} onChange={handleChange} className={errors.board10 ? 'error' : ''} />
              {errors.board10 && <span className="error-text">{errors.board10}</span>}
            </div>
          </div>
        )}

        {/* Step 4: Aadhaar and APAAR ID */}
        {step === 4 && (
          <div className="form-step">
            <h2>Aadhaar and APAAR ID</h2>
            <div className="form-group">
              <label>Aadhaar Number*</label>
              <input
                type="text"
                name="adhaar_id"
                value={formData.adhaar_id}
                onChange={handleChange}
                className={errors.adhaar_id ? 'error' : ''}
              />
              {errors.adhaar_id && <span className="error-text">{errors.adhaar_id}</span>}
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <label>APAAR ID*</label>
                <a
                  href="https://apaar.education.gov.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                // style={{ fontSize: '12px' }}
                >
                  (Click here to create APAAR ID)
                </a>
              </div>
              <input
                type="text"
                name="apaar_id"
                value={formData.apaar_id}
                onChange={handleChange}
                className={errors.apaar_id ? 'error' : ''}
              />
              {errors.apaar_id && <span className="error-text">{errors.apaar_id}</span>}
            </div>
            <div className="form-group">
              <label>Student College ID Card* (PDF or Image only, max 5MB)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                name="student_college_idcard"
                onChange={handleChange}
                className={errors.student_college_idcard ? 'error' : ''}
              />
              {formData.student_college_idcard && (
                <div className="file-info">
                  <p>
                    Selected: {formData.student_college_idcard.name}
                    ({(formData.student_college_idcard.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              )}
              {errors.student_college_idcard && <span className="error-text">{errors.student_college_idcard}</span>}
            </div>
            <div className="form-group">
              <label>Other Documents (Please upload these documents: all  marksheets from std 10,dept NOC from Hod, resume and SOP; file size max 10MB as a single pdf)</label>
              <input
                type="file"
                name="documents"
                onChange={handleChange}
                accept=".pdf"
                className={errors.documents ? 'error' : ''}
              />
              {formData.documents && (
                <div className="file-info">
                  <p>
                    Selected: {formData.documents.name}
                    ({(formData.documents.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              )}
              {errors.documents && <span className="error-text">{errors.documents}</span>}
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div className="form-step">
            <h2>Review Your Information</h2>
            <div className="review-section">
              <h3>Personal Information</h3>
              <p><strong>Name:</strong> {formData.name}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
              <p><strong>Date of Birth:</strong> {formData.dob}</p>
              <p><strong>Address:</strong> {formData.address}</p>
              <p><strong>State:</strong> {formData.state}</p>
              <p><strong>Guardian Name:</strong> {formData.guardianName}</p>
              <p><strong>Guardian Relation:</strong> {formData.guardianRelation}</p>
              <p><strong>Guardian Phone:</strong> {formData.guardianPhone}</p>
              <h3>Academic Information</h3>
              <p><strong>Institution:</strong> {formData.institution}</p>
              <p><strong>Program:</strong> {formData.program}</p>
              <p><strong>Department:</strong> {formData.department}</p>
              <p><strong>Year:</strong> {formData.year}</p>
              <p><strong>Institute Location:</strong> {formData.instituteLocation}</p>
              <p><strong>Institute State:</strong> {formData.instituteState}</p>
              <p><strong>Current Semester Percentage:</strong> {formData.currentSemesterCgpa}</p>
              <p><strong>UG Details:</strong> {formData.UG}</p>
              <p><strong>12th Percentage:</strong> {formData.cgpa12}</p>
              <p><strong>12th Board:</strong> {formData.board12}</p>
              <p><strong>10th Percentage:</strong> {formData.cgpa10}</p>
              <p><strong>10th Board:</strong> {formData.board10}</p>
              <h3>IDs</h3>
              <p><strong>Aadhaar Number:</strong> {formData.adhaar_id}</p>
              <p><strong>APAAR ID:</strong> {formData.apaar_id}</p>
              <p><strong>Student College ID Card:</strong> {formData.student_college_idcard ? formData.student_college_idcard.name : "Not uploaded"}</p>
              <p><strong>Other Documents:</strong> {formData.documents ? formData.documents.name : "Not uploaded"}</p>
            </div>
          </div>
        )}

        <div className="form-navigation">
          {step > 1 && (
            <button type="button" onClick={handleBack} className="back-button">
              Back
            </button>
          )}
          {step < 5 ? (
            <button type="button" onClick={handleNext} className="next-button">
              Next
            </button>
          ) : (
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default Registerform;